import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Platform from './pages/Platform';
import Access from './pages/Access';
import ConnectWallet from './components/ConnectWallet';
import ToastContainer from './components/Toast';
import { useWalletBalances } from './services/walletBalanceService';
import { useGRPCConnector } from './utils/gRPCConnector';
import Trade2 from './pages/Trade2';
import solanaWebSocketManager from './services/solanaWebSocketManager';
import { setupQuickBuyEventListener } from './services/quickBuyService';

// Import the new architectural HomePage instead of Home
import HomePage from './pages/HomePage';
import About from './pages/About';
import Features from './pages/Features';
import Team from './pages/Team';
import Network from './pages/Network';
import Careers from './pages/Careers';
import Giveaways from './pages/Giveaways';
import Docs from './pages/Docs';
import Showcase from './pages/Showcase';
import Community from './pages/Community';

// Import the new pages we created
import Roadmap from './pages/Roadmap';
import Partners from './pages/Partners';
import Whitepaper from './pages/Whitepaper';
import Tokenomics from './pages/Tokenomics';
import PlatformSelection from './pages/PlatformSelection';
import Modules from './pages/Modules';
import PortfolioPage from './pages/PortfolioPage';
import WalletsPage from './pages/WalletsPage';
import AdvancedChartsPage from './pages/AdvancedChartsPage';

// Import the ArchitecturalLines component
import ArchitecturalLines from './components/ArchitecturalLines';
import StoryboardIntro from './components/StoryboardIntro';

import serviceInitializer from './services/serviceInitializer';

// Remove the global declaration and just create wallet types
type SolanaWallet = {
  isConnected: boolean;
  publicKey: { toString(): string };
  disconnect(): Promise<void>;
};

type SolflareWallet = {
  isConnected: boolean;
  publicKey: { toString(): string };
  disconnect(): Promise<void>;
};

function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'access' | 'platform' | 'platform-selection' | 'modules' | 'portfolio-page' | 'wallets-page' | 'charts-page' | 'trade2' | 'about' | 'features' | 'team' | 'network' | 'careers' | 'giveaways' | 'docs' | 'guide' | 'community' | 'roadmap' | 'partners' | 'whitepaper' | 'tokenomics'>('home');
  const [currentView, setCurrentView] = useState<'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community'>('trade');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [walletProvider, setWalletProvider] = useState<'phantom' | 'solflare' | null>(null);
  const [isFading, setIsFading] = useState(false);
  const [showStoryboard, setShowStoryboard] = useState(false);
  const [hasSeenIntro, setHasSeenIntro] = useState(false);
  
  const { subscribeToAddress, unsubscribeFromAddress, isConnected } = useGRPCConnector();
  const { refreshBalances } = useWalletBalances();

  // Map URLs to pages
  const getPageFromPath = (path: string): typeof currentPage => {
    const cleanPath = path.toLowerCase().replace(/^\/+|\/+$/g, '');
    
    switch (cleanPath) {
      case '':
      case 'home':
        return 'home';
      case 'access':
        return 'access';
      case 'platform':
        return 'platform';
      case 'platform-selection':
        return 'platform-selection';
      case 'modules':
        return 'modules';
      case 'portfolio-page':
        return 'portfolio-page';
      case 'wallets-page':
        return 'wallets-page';
      case 'charts-page':
        return 'charts-page';
      case 'trade2':
        return 'trade2';
      case 'about':
        return 'about';
      case 'features':
        return 'features';
      case 'team':
        return 'team';
      case 'network':
        return 'network';
      case 'careers':
        return 'careers';
      case 'giveaways':
        return 'giveaways';
      case 'docs':
        return 'docs';
      case 'showcase':
        return 'showcase';
      case 'community':
        return 'community';
      case 'roadmap':
        return 'roadmap';
      case 'partners':
        return 'partners';
      case 'whitepaper':
        return 'whitepaper';
      case 'tokenomics':
        return 'tokenomics';
      default:
        return 'home';
    }
  };

  // Initialize page from URL on load
  useEffect(() => {
    const initialPage = getPageFromPath(window.location.pathname);
    setCurrentPage(initialPage);
    
    // Show storyboard every time user visits home page
    if (initialPage === 'home') {
      setShowStoryboard(true);
    }
    
    // Listen for browser back/forward buttons
    const handlePopState = () => {
      const newPage = getPageFromPath(window.location.pathname);
      setCurrentPage(newPage);
      
      // Show storyboard when navigating to home
      if (newPage === 'home') {
        setShowStoryboard(true);
      }
      
      window.scrollTo(0, 0);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Initialize app on startup
  useEffect(() => {
    // 🔥 **NEW**: Initialize SolanaWebSocketManager for direct SolanaTracker connections
    console.log('App: Initializing SolanaWebSocketManager...');
    
    const initializeWebSocket = async () => {
      try {
        await solanaWebSocketManager.initialize();
        console.log('App: Successfully connected to SolanaTracker WebSocket service');
      } catch (error) {
        console.error('App: Failed to connect to SolanaTracker WebSocket service:', error);
      }
    };
    
    initializeWebSocket();

    // Set up the quick buy event listener
    setupQuickBuyEventListener();

    // Cleanup function to handle component unmount
    return () => {
      console.log('App: Cleaning up WebSocket connections...');
      solanaWebSocketManager.cleanup();
    };
  }, []);

  // Set up cleanup on window unload to properly disconnect WebSockets
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      // Clean up WebSocket connections
      console.log('App: Window unload - cleaning up WebSocket connections');
      solanaWebSocketManager.cleanup();
    });
  }

  // Handle wallet connection
  const handleWalletConnected = async (address: string, provider: 'phantom' | 'solflare') => {
    setWalletAddress(address);
    setWalletProvider(provider);
    
    // Save wallet address to localStorage for quick buy
    localStorage.setItem('walletAddress', address);
    
    if (isConnected) {
      try {
        await subscribeToAddress(address);
        await refreshBalances(address);
        console.log(`App: Subscribed to wallet updates for ${address}`);
      } catch (error) {
        console.error("App: Error subscribing to wallet updates:", error);
      }
    }
    
    setShowConnectModal(false);
  };
  
  // Handle wallet disconnection
  const handleWalletDisconnected = async () => {
    if (walletAddress && isConnected) {
      try {
        await unsubscribeFromAddress(walletAddress);
        console.log(`App: Unsubscribed from wallet ${walletAddress}`);
      } catch (error) {
        console.error("App: Error unsubscribing from wallet:", error);
      }
    }
    
    // Remove wallet address from localStorage
    localStorage.removeItem('walletAddress');
    
    // Disconnect wallet providers
    if (window.solana) {
      try {
        await (window.solana as SolanaWallet).disconnect();
      } catch (e) {
        console.error("Error disconnecting Phantom:", e);
      }
    }
    
    if (window.solflare) {
      try {
        await (window.solflare as SolflareWallet).disconnect();
      } catch (e) {
        console.error("Error disconnecting Solflare:", e);
      }
    }
    
    setWalletAddress('');
    setWalletProvider(null);
  };
  
  // Check wallet connection on page load and reconnect if needed
  useEffect(() => {
    const checkWalletConnection = async () => {
      // Check if Phantom is connected
      if (window.solana?.isConnected && window.solana?.publicKey) {
        const address = window.solana.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('phantom');
        
        if (isConnected) {
          try {
            await subscribeToAddress(address);
            await refreshBalances(address);
            console.log(`App: Reconnected to Phantom wallet ${address}`);
          } catch (error) {
            console.error("App: Error reconnecting to wallet:", error);
          }
        }
        return;
      }
      
      // Check if Solflare is connected
      if (window.solflare?.isConnected && window.solflare?.publicKey) {
        const address = window.solflare.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('solflare');
        
        if (isConnected) {
          try {
            await subscribeToAddress(address);
            await refreshBalances(address);
            console.log(`App: Reconnected to Solflare wallet ${address}`);
          } catch (error) {
            console.error("App: Error reconnecting to wallet:", error);
          }
        }
        return;
      }
    };
    
    checkWalletConnection();
  }, [isConnected, subscribeToAddress, refreshBalances]);

  // Helper function to handle smooth page transitions with URL updates
  const navigateToPage = (targetPage: string) => {
    if (targetPage === currentPage) return; // Don't transition to same page
    
    // Update URL without page reload
    const newPath = targetPage === 'home' ? '/' : `/${targetPage}`;
    window.history.pushState(null, '', newPath);
    
    setIsFading(true);
    
    setTimeout(() => {
      if (targetPage === 'access') {
        setCurrentPage('access');
      } else if (targetPage === 'home') {
        setCurrentPage('home');
      } else if (targetPage === 'platform') {
        setCurrentPage('platform');
        setCurrentView('trade');
      } else if (targetPage === 'platform-selection') {
        setCurrentPage('platform-selection');
      } else if (targetPage === 'modules') {
        setCurrentPage('modules');
      } else if (targetPage === 'portfolio-page') {
        setCurrentPage('portfolio-page');
      } else if (targetPage === 'wallets-page') {
        setCurrentPage('wallets-page');
      } else if (targetPage === 'charts-page') {
        setCurrentPage('charts-page');
      } else if (targetPage === 'trade2') {
        setCurrentPage('trade2');
        setCurrentView('trade2');
      } else {
        setCurrentPage(targetPage as any);
      }
      window.scrollTo(0, 0);
      
      setTimeout(() => {
        setIsFading(false);
      }, 50);
    }, 200);
  };

  const handleLaunchPlatform = () => {
    navigateToPage('access');
  };
  
  const handleGoHome = () => {
    navigateToPage('home');
  };

  // Add navigation function for the new pages
  const handleNavigateToPage = (page: 'about' | 'features' | 'team' | 'network' | 'careers' | 'giveaways' | 'docs' | 'showcase' | 'community' | 'roadmap' | 'partners' | 'whitepaper' | 'tokenomics') => {
    navigateToPage(page);
  };

  const handleContinueToPlatform = () => {
    navigateToPage('platform-selection');
  };

  const handleSelectCanvasUI = () => {
    navigateToPage('platform');
  };

  const handleSelectModules = () => {
    navigateToPage('modules');
  };

  const handleBackToPlatformSelection = () => {
    navigateToPage('platform-selection');
  };

  const handleModuleNavigation = (route: string) => {
    navigateToPage(route);
  };

  // Handle view change for platform sections
  const handleViewChange = (view: 'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community') => {
    // If we're not on the platform page, navigate there with transition
    if (view === 'trade2' && currentPage !== 'trade2') {
      navigateToPage('trade2');
    } else if (currentPage !== 'platform' && view !== 'trade2') {
      navigateToPage('platform');
    } else {
      // Same page, just change view without transition
      setCurrentView(view);
    }
  };

  // Storyboard handlers
  const handleStoryboardComplete = () => {
    setShowStoryboard(false);
  };

  const handleStoryboardSkip = () => {
    setShowStoryboard(false);
  };

  return (
    <div className="app-container min-h-screen relative bg-white text-gray-900">
      {/* Storyboard Intro - DISABLED */}
      {/* {showStoryboard && (
        <StoryboardIntro
          onComplete={handleStoryboardComplete}
          onSkip={handleStoryboardSkip}
        />
      )} */}

      {/* Global architectural background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        {/* Subtle background orbs for light theme */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-green-100/30 blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-green-200/20 blur-[100px] animate-pulse-slow delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] rounded-full bg-gray-100/40 blur-[80px] animate-pulse-slow delay-2000"></div>
        <div className="absolute bottom-1/3 left-1/4 w-[450px] h-[450px] rounded-full bg-green-100/25 blur-[90px] animate-pulse-slow delay-3000"></div>
      </div>
      
      {/* Architectural connecting lines */}
      <ArchitecturalLines density="medium" animate={true} showCrosses={true} />
      
      {/* Consistent Navbar across all pages */}
      <Navbar 
        onLaunchPlatform={handleLaunchPlatform} 
        isHomePage={currentPage === 'home'}
        isAccessPage={currentPage === 'access'}
        isContentPage={['about', 'features', 'team', 'network', 'careers', 'giveaways', 'docs', 'guide', 'community', 'roadmap', 'partners', 'whitepaper', 'tokenomics'].includes(currentPage)}
        walletAddress={walletAddress}
        walletProvider={walletProvider === null ? undefined : walletProvider}
        onShowConnectModal={() => setShowConnectModal(true)}
        onDisconnectWallet={handleWalletDisconnected}
        onGoHome={handleGoHome}
        onNavigateToPage={handleNavigateToPage}
        currentView={currentView}
        onViewChange={handleViewChange}
        isOnPlatformPage={currentPage === 'platform' || currentPage === 'trade2'}
        className="z-50"
      />
      
      {/* Main Content with Fade Transition */}
      <div className={`transition-opacity duration-200 ${isFading ? 'opacity-0' : 'opacity-100'}`}>
      
      {currentPage === 'home' ? (
        <main className="relative z-10">
          <HomePage onLaunchPlatform={handleLaunchPlatform} onNavigateToPage={handleNavigateToPage} />
        </main>
      ) : currentPage === 'access' ? (
        <div className="relative z-10">
          <Access 
            onGoHome={handleGoHome}
            onLaunchPlatform={handleContinueToPlatform}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'platform-selection' ? (
        <div className="relative z-10">
          <PlatformSelection 
            onGoHome={handleGoHome}
            onSelectCanvasUI={handleSelectCanvasUI}
            onSelectModules={handleSelectModules}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'modules' ? (
        <div className="relative z-10">
          <Modules 
            onGoBack={handleBackToPlatformSelection}
            onGoHome={handleGoHome}
            onNavigateToModule={handleModuleNavigation}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'portfolio-page' ? (
        <div className="relative z-10">
          <PortfolioPage 
            onGoBack={() => navigateToPage('modules')}
            onGoHome={handleGoHome}
            onNavigateToModule={handleModuleNavigation}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'wallets-page' ? (
        <div className="relative z-10">
          <WalletsPage 
            onGoBack={() => navigateToPage('modules')}
            onGoHome={handleGoHome}
            onNavigateToModule={handleModuleNavigation}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'charts-page' ? (
        <div className="relative z-10">
          <AdvancedChartsPage 
            onGoBack={() => navigateToPage('modules')}
            onGoHome={handleGoHome}
            onNavigateToModule={handleModuleNavigation}
            walletAddress={walletAddress || undefined}
            walletProvider={walletProvider || undefined}
            onShowConnectModal={() => setShowConnectModal(true)}
            onDisconnectWallet={handleWalletDisconnected}
          />
        </div>
      ) : currentPage === 'about' ? (
        <main className="relative z-10">
          <About onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'features' ? (
        <main className="relative z-10">
          <Features onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'team' ? (
        <main className="relative z-10">
          <Team onNavigateToPage={handleNavigateToPage} onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'network' ? (
        <main className="relative z-10">
          <Network onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'careers' ? (
        <main className="relative z-10">
          <Careers onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'giveaways' ? (
        <main className="relative z-10">
          <Giveaways onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'docs' ? (
        <main className="relative z-10">
          <Docs onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'showcase' ? (
        <main className="relative z-10">
          <Showcase onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'community' ? (
        <main className="relative z-10">
          <Community onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'roadmap' ? (
        <main className="relative z-10">
          <Roadmap onNavigateToPage={handleNavigateToPage} onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'partners' ? (
        <main className="relative z-10">
          <Partners onNavigateToPage={handleNavigateToPage} onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'whitepaper' ? (
        <main className="relative z-10">
          <Whitepaper onNavigateToPage={handleNavigateToPage} onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'tokenomics' ? (
        <main className="relative z-10">
          <Tokenomics onNavigateToPage={handleNavigateToPage} onLaunchPlatform={handleLaunchPlatform} />
        </main>
      ) : currentPage === 'trade2' ? (
        <Trade2 
          walletAddress={walletAddress}
          walletProvider={walletProvider}
        />
      ) : (
        <Platform 
          onGoHome={handleGoHome}
          walletAddress={walletAddress}
          walletProvider={walletProvider}
          onShowConnectModal={() => setShowConnectModal(true)}
          onDisconnectWallet={handleWalletDisconnected}
          currentView={currentView}
          onViewChange={handleViewChange}
        />
      )}
      </div>
      
      {/* Show ConnectWallet modal on all pages */}
      <ConnectWallet 
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onWalletConnected={handleWalletConnected}
      />

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
}

export default App